/**
 * src/components/Financial/modules/PlanetaryTrendlineModule.jsx
 * 【完全修正版：ダークモード対応・デザイン統一】
 */
import React from "react";
import { TrendingUp, Info } from "lucide-react";
import * as Astro from "../../../astronomy.js";
import { PLANET_SYMBOLS, PLANET_COLORS } from "../../../utils/constants.js";

/**
 * 複数惑星のトレンドライン計算ロジック
 */
export const calculatePlanetaryTrendlines = function (
  timeline,
  baseIndex,
  basePrice,
  config,
  isHelio,
) {
  if (baseIndex === null || !timeline[baseIndex] || !config.selectedPlanets)
    return {};

  var baseRow = timeline[baseIndex];
  var baseDate = baseRow.timestamp
    ? new Date(baseRow.timestamp)
    : new Date(baseRow.Date);
  var multiplier = parseFloat(config.multiplier) || 10;
  var planets = Array.from(config.selectedPlanets);

  var results = {};

  planets.forEach(function (planet) {
    var baseLon = isHelio
      ? Astro.getHeliocentricLongitude(planet, baseDate)
      : Astro.getPlanetLongitude(planet, baseDate);

    if (baseLon === null || baseLon === undefined) return;

    var points = [];
    var cumulativeDelta = 0; // 基準点からの累積移動量
    var lastLon = baseLon; // 直前の計算で使用した経度

    for (var i = baseIndex; i < timeline.length; i++) {
      var currentRow = timeline[i];
      var currentDate = currentRow.timestamp
        ? new Date(currentRow.timestamp)
        : new Date(currentRow.Date);

      var currentLon = isHelio
        ? Astro.getHeliocentricLongitude(planet, currentDate)
        : Astro.getPlanetLongitude(planet, currentDate);

      if (currentLon !== null && currentLon !== undefined) {
        var stepDelta = currentLon - lastLon;
        if (stepDelta > 180) stepDelta -= 360;
        if (stepDelta < -180) stepDelta += 360;
        cumulativeDelta += stepDelta;
        lastLon = currentLon;
      }

      var priceOffset = cumulativeDelta * multiplier;
      points.push({
        index: i,
        price: parseFloat(basePrice) + priceOffset,
      });
    }
    results[planet] = points;
  });

  return results;
};

/**
 * 複数ラインの描画レイヤー（ダークモード対応・高視認性バッジUI）
 */
export const PlanetaryTrendlineLayer = function (props) {
  var show = props.show;
  var results = props.results || {};
  var coords = props.coords;
  var displayRange = props.displayRange;
  var isDarkMode = props.isDarkMode; // ★ダークモード判定を受け取る

  if (!show) return null;

  var minY = coords.pad.t;
  var maxY = coords.pad.t + coords.iH;
  var maxX = coords.getX(displayRange.count - 1);

  return (
    <g className="planetary-trendline-layer">
      {Object.keys(results).map(function (planet) {
        var points = results[planet];
        var color = PLANET_COLORS[planet] || "#6366f1";

        var visiblePoints = points.filter(function (p) {
          return (
            p.index >= displayRange.start &&
            p.index < displayRange.start + displayRange.count
          );
        });

        if (visiblePoints.length < 2) return null;

        var polyPoints = visiblePoints
          .map(function (p) {
            return (
              coords.getX(p.index - displayRange.start) +
              "," +
              coords.getY(p.price)
            );
          })
          .join(" ");

        var lastP = visiblePoints[visiblePoints.length - 1];
        var firstP = visiblePoints[0];
        var xStart = coords.getX(firstP.index - displayRange.start);
        var yStart = coords.getY(firstP.price);
        var xEnd = coords.getX(lastP.index - displayRange.start);
        var yEnd = coords.getY(lastP.price);

        var symbolX = xEnd;
        var symbolY = yEnd;

        if (xEnd > xStart) {
          var slope = (yEnd - yStart) / (xEnd - xStart);
          if (symbolY < minY) {
            symbolY = minY + 12;
            symbolX = xStart + (symbolY - yStart) / slope;
          } else if (symbolY > maxY) {
            symbolY = maxY - 12;
            symbolX = xStart + (symbolY - yStart) / slope;
          }
          if (symbolX > maxX) {
            symbolX = maxX;
            symbolY = yStart + (symbolX - xStart) * slope;
            if (symbolY < minY) symbolY = minY + 12;
            if (symbolY > maxY) symbolY = maxY - 12;
          }
        }

        return (
          <g key={"p-trend-group-" + planet}>
            {/* トレンドラインのフチ取り（背景色に合わせる） */}
            <polyline
              points={polyPoints}
              fill="none"
              stroke={isDarkMode ? "#0b0e14" : "#ffffff"}
              strokeWidth="2.5"
              opacity="0.4"
            />
            <polyline
              points={polyPoints}
              fill="none"
              stroke={color}
              strokeWidth="1.2"
              strokeDasharray="2,2"
              opacity="0.8"
            />

            <g transform={"translate(" + (symbolX + 2) + "," + symbolY + ")"}>
              {/* バッジの背景円 */}
              <circle
                r="9"
                fill={color}
                stroke={isDarkMode ? "#0b0e14" : "#ffffff"}
                strokeWidth="1.5"
              />
              <text
                fill="#fff"
                fontSize="11"
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="central"
              >
                {PLANET_SYMBOLS[planet]}
              </text>
            </g>
          </g>
        );
      })}
    </g>
  );
};

/**
 * 惑星トレンドライン 設定UI
 */
export const PlanetaryTrendlineSettingsUI = function (props) {
  var show = props.show;
  var setShow = props.setShow;
  var config = props.config;
  var setConfig = props.setConfig;
  var isDarkMode = props.isDarkMode;

  var planets = [
    "Sun",
    "Moon",
    "Mercury",
    "Venus",
    "Mars",
    "Jupiter",
    "Saturn",
    "Uranus",
    "Neptune",
    "Pluto",
  ];

  // テーマ定義の統一
  var theme = {
    cardBg: isDarkMode ? "#1c1f26" : "#f8fafc",
    border: isDarkMode ? "#2d313d" : "#e2e8f0",
    text: isDarkMode ? "#ffffff" : "#1e293b",
    subText: isDarkMode ? "rgba(255, 255, 255, 0.4)" : "#64748b",
    inputBg: isDarkMode ? "#0b0e14" : "#ffffff",
    inputBorder: isDarkMode ? "#374151" : "#cbd5e1",
  };

  var togglePlanet = function (p) {
    var next = new Set(config.selectedPlanets);
    if (next.has(p)) next.delete(p);
    else next.add(p);
    setConfig(Object.assign({}, config, { selectedPlanets: next }));
  };

  /**
   * ボタン共通スタイル (f98.jpg に合わせた統一デザイン)
   */
  var btnSt = function (active) {
    return {
      padding: "6px 20px",
      fontSize: "11px",
      fontWeight: "bold",
      borderRadius: "4px",
      cursor: "pointer",
      background: active ? "#2c2d96" : isDarkMode ? "#2d2c33" : "#ffffff",
      color: active ? "#fff" : isDarkMode ? "#ccc" : "#64748b",
      border:
        "1px solid " +
        (active ? "#2c2d96" : isDarkMode ? "rgba(255,255,255,0.1)" : "#cbd5e1"),
      transition: "all 0.2s",
    };
  };

  /**
   * 天体選択ボタンの共通スタイル
   */
  var plBtnSt = function (p, active) {
    var pColor = PLANET_COLORS[p] || "#6366f1";
    return {
      display: "flex",
      alignItems: "center",
      gap: "6px",
      padding: "6px 14px",
      borderRadius: "8px",
      fontSize: "12px",
      fontWeight: "600",
      cursor: "pointer",
      background: active
        ? isDarkMode
          ? "#374151"
          : "#fff"
        : isDarkMode
          ? "transparent"
          : "#f1f5f9",
      border:
        "1.5px solid " +
        (active ? pColor : isDarkMode ? "rgba(255,255,255,0.05)" : "#e2e8f0"),
      color: active
        ? isDarkMode
          ? "#fff"
          : pColor
        : isDarkMode
          ? "rgba(255, 255, 255, 0.4)"
          : "#64748b",
    };
  };

  return (
    <div
      style={{
        background: theme.cardBg,
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + theme.border,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: theme.text,
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <TrendingUp size={18} color={isDarkMode ? "#ffffff" : "#2c2d96"} />
          <span>惑星トレンドライン (Planetary Trendline)</span>
        </div>
        <button onClick={() => setShow(!show)} style={btnSt(show)}>
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
        }}
      >
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              fontSize: "11px",
              color: theme.subText,
              display: "block",
              marginBottom: "8px",
              fontWeight: "bold",
            }}
          >
            1度あたりの価格 (Points/Degree)
          </label>
          <input
            type="number"
            value={config.multiplier}
            onChange={(e) =>
              setConfig(
                Object.assign({}, config, { multiplier: e.target.value }),
              )
            }
            style={{
              padding: "8px 12px",
              background: theme.inputBg,
              color: theme.text,
              border: "1px solid " + theme.inputBorder,
              borderRadius: "6px",
              width: "120px",
              fontSize: "12px",
              outline: "none",
            }}
          />
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {planets.map((p) => {
            var isSelected =
              config.selectedPlanets && config.selectedPlanets.has(p);
            return (
              <button
                key={p}
                onClick={() => togglePlanet(p)}
                style={plBtnSt(p, isSelected)}
              >
                <span style={{ fontSize: "14px" }}>{PLANET_SYMBOLS[p]}</span>{" "}
                {p}
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          color: theme.subText,
          fontSize: "11px",
          marginTop: "20px",
          paddingTop: "15px",
          borderTop:
            "1px solid " + (isDarkMode ? "rgba(255,255,255,0.05)" : "#f1f5f9"),
        }}
      >
        <Info size={14} />
        <span>
          ダブルクリックした起点を 0°
          として、天体の移動量に応じた価格ラインを境界内で描画します。
        </span>
      </div>
    </div>
  );
};
